import { type Page, type PageResolver, router } from "@inertiajs/core";
import { MetaProvider } from "@solidjs/meta";
import {
  type Component,
  type ParentComponent,
  type ParentProps,
  type JSX,
  batch,
  createComponent,
  createMemo,
  mergeProps,
} from "solid-js";
import { createStore, reconcile, type SetStoreFunction } from "solid-js/store";
import { isServer } from "solid-js/web";
import PageContext from "./PageContext";
import { setLocations } from "~/util/location_registry";
import { installCrossLocationInertiaGuard } from "~/util/location_switch_guard";

// Matches the Enterprise Users index (not a user's detail page at .../users/:id).
const ENTERPRISE_USERS_INDEX_PATH = /^\/enterprise\/accnt_[a-z0-9]+\/users$/;

// Editing a user happens in a dialog on the detail page; after saving, the user
// stays there. Pressing browser Back restores the Users index from Inertia's
// history cache, so the edit wouldn't be reflected. When Back/Forward lands on
// that index, refresh just the list props from the server.
//
// We react to Inertia's `navigate` event, not the raw popstate: with
// `encrypt_history` enabled, Inertia restores history asynchronously (it must
// decrypt first), so reloading straight from popstate would race — and get
// clobbered by — the restore. `navigate` fires only after the restore settles.
// A popstate flag limits the reload to Back/Forward; ordinary link visits
// already fetch fresh, and reloading them would double-fetch. Registered once
// at boot so it is armed before any navigation back into the index occurs.
function installUsersListRefreshOnBack() {
  let cameFromHistoryNav = false;
  window.addEventListener("popstate", () => {
    cameFromHistoryNav = true;
  });

  router.on("navigate", (event) => {
    const wasHistoryNav = cameFromHistoryNav;
    cameFromHistoryNav = false;
    if (!wasHistoryNav) return;

    const path = event.detail.page.url.split("?")[0] ?? "";
    if (ENTERPRISE_USERS_INDEX_PATH.test(path)) {
      router.reload({ only: ["users", "pendingInvitations"] });
    }
  });
}

function extractLocationsFromProps(props: Record<string, any>): { id: string; name: string }[] {
  // EmbeddedInertia: auth.locations is a flat array
  const authLocations = props["auth"]?.locations;
  if (Array.isArray(authLocations) && authLocations.length > 0) {
    return authLocations as { id: string; name: string }[];
  }

  // PortalSwitcher: switcher.accounts[].locations[] is nested by account
  const accounts = props["switcher"]?.accounts;
  if (Array.isArray(accounts)) {
    return (accounts as { locations?: { id: string; name: string }[] }[]).flatMap(
      (account) => account.locations ?? [],
    );
  }

  return [];
}

export type InertiaAppProps = {
  initialPage: Page;
  initialComponent?: Component<Page["props"]> & {
    layout?: ParentComponent<any> | ParentComponent<any>[];
  };
  resolveComponent?: PageResolver;
};

type ShellState = {
  component: InertiaAppProps["initialComponent"] | null;
  layouts: ParentComponent<any>[];
  key: any;
};

type PropsFrame = {
  props: Page["props"];
  setProps: SetStoreFunction<Page["props"]>;
};

function createPropsFrame(props: Page["props"]): PropsFrame {
  const [frameProps, setFrameProps] = createStore<Page["props"]>({ ...props });

  return {
    props: frameProps,
    setProps: setFrameProps,
  };
}

/* @ts-ignore */
function extractLayouts(component) {
  if (!component) {
    return [];
  }

  if (typeof component.layout === "function") {
    return [component.layout];
  }

  if (Array.isArray(component.layout)) {
    return component.layout;
  }

  return [];
}

export default function App(props: ParentProps<InertiaAppProps>) {
  const [shell, setShell] = createStore<ShellState>({
    component: props.initialComponent || null,
    layouts: [...extractLayouts(props.initialComponent || null)],
    key: null,
  });

  // `usePage()` needs a stable context value identity so layouts can react to
  // URL/prop changes without remounting. Keep this mutable store separate from
  // frame-scoped page props used by page/layout render props.
  const [contextPage, setContextPage] = createStore<Page>({
    ...props.initialPage,
    props: { ...props.initialPage.props },
  });

  // Keep render props in a frame-scoped store.
  //
  // Real examples this protects:
  // - Clinical note template prompt CRUD partially reloads `props.prompts`; we must
  //   keep in-progress template/editor text (no remount on preserveState updates).
  // - Knowledge doc show -> index: old page has `<Title>{props.document.title}</Title>`
  //   while next page has `document = null`; old effects must not read next page props.
  let propsFrame = createPropsFrame(props.initialPage.props);

  if (!isServer) {
    const initialProps = props.initialPage.props as Record<string, any>;
    const initialLocations = extractLocationsFromProps(initialProps);
    if (initialLocations.length > 0) setLocations(initialLocations);
    installCrossLocationInertiaGuard();
    installUsersListRefreshOnBack();

    router.init({
      initialPage: { ...props.initialPage },
      /* @ts-ignore */
      resolveComponent: props.resolveComponent,
      async swapComponent({ component, page, preserveState }) {
        const pageProps = page.props as Record<string, any>;
        const pageLocations = extractLocationsFromProps(pageProps);
        if (pageLocations.length > 0) setLocations(pageLocations);
        const preserveFrame = preserveState && page.component === contextPage.component;

        if (preserveFrame) {
          propsFrame.setProps(reconcile(page.props));
        } else {
          propsFrame = createPropsFrame(page.props);
        }

        batch(() => {
          setShell(
            reconcile({
              component: component as Component,
              // Spread to create a new array - ensures reconcile detects layout changes
              // when transitioning between pages with different layout depths
              layouts: [...extractLayouts(component)],
              key: preserveFrame ? shell.key : Date.now(),
            }),
          );

          setContextPage(reconcile(page));
        });
      },
    });
  }

  const renderPage = (i = 0): JSX.Element => {
    // Each depth remounts only when ITS layout changes. Reading the array length here, in the scope
    // that renders this depth, remounted the outermost layout whenever the depth changed (an RCM
    // board at [Layout] to a report at [Layout, RcmReportsLayout]), and with it everything a layout
    // keeps alive across visits: the RCM softphone's live call and inbound receiver among them.
    const layout = createMemo(() => (i < shell.layouts.length ? shell.layouts[i] : undefined));

    return createMemo(() => {
      const current = layout();
      if (!current) {
        return createComponent(
          /* @ts-ignore */
          shell.component,
          mergeProps(
            {
              key: shell.key,
            },
            propsFrame.props,
          ),
        );
      }

      return createComponent(
        current,
        mergeProps(propsFrame.props, {
          get children() {
            return renderPage(i + 1);
          },
        }),
      );
    }) as unknown as JSX.Element;
  };

  const renderPageFrame = () =>
    createComponent(PageContext.Provider, {
      value: contextPage,
      get children() {
        return renderPage();
      },
    });

  return createComponent(MetaProvider, {
    get children() {
      return renderPageFrame();
    },
  });
}
